/**
 * Single source of truth for the event start/end date rules, mirroring the
 * backend `EventService` guards (DATE_PAST_SLACK = 1 day, DATE_FUTURE_CAP =
 * 20 years) plus the paid-plan window cap. EVERY surface that lets a human OR
 * the Velo AI set an event start/end must run this so an out-of-window date is
 * rejected BEFORE the create/PUT 400s with `invalidDateRange`.
 *
 * Historically this rule was hand-copied into GeneralInformationStep and
 * ContentGeneralCard, while the AI wizard tool had NO bound check at all — so
 * the model could seed a date before today (audit 2026-07-09). Centralised here
 * so the AI path and the two human paths can never drift again.
 */

/** Backend slack: a start/end may sit up to 1 day in the past. */
export const EVENT_DATE_PAST_SLACK_MS = 24 * 60 * 60 * 1000;
/** Backend cap: a start/end may sit at most ~20 years in the future. */
export const EVENT_DATE_FUTURE_CAP_MS = 20 * 365 * 24 * 60 * 60 * 1000;

/** Error keys live in the `errors` i18n namespace. */
export type EventDateBoundsError =
  | "startDateEndDate"
  | "startDateInPast"
  | "startDateTooFar"
  | "endDateInPast"
  | "endDateTooFar"
  | "eventDateAfterPlanExpiry";

export type EventDateBoundsOptions = {
  /**
   * Paid events (non-null plan expiry) may not be scheduled past their plan
   * window — it would lock before it runs. FREE / grandfathered events pass
   * `null`/`undefined` and keep only the 20-year cap.
   */
  planExpiresAt?: Date | string | null;
  /** Reference "now" — defaults to the real clock. Injectable for tests. */
  now?: Date;
  /**
   * EDIT surfaces only. The dates the user has NOT moved — i.e. the ones that
   * will NOT be in the PUT payload, because `getChangedFieldsEvent` omits them.
   * Their SINGLE-INSTANT rules (past slack, 20-year cap, plan-expiry cap) are
   * skipped, mirroring the backend, which bounds "only the fields in the
   * payload" (EventService F9 and the plan-window block are both
   * `event.getX() != null`-gated). Without this, an event that had already
   * started could not be prolonged at all: the end picker re-ran the
   * start-in-past rule over an untouched start and disabled Save.
   *
   * The ORDERING rule (start <= end) is NEVER skipped — it is about the pair,
   * not one instant, and the backend enforces it against the MERGED
   * start/end, answering with a raw English sentence that ErrorFormater
   * cannot map to a code (the user would see a bare "validationError").
   *
   * CREATE and AI surfaces pass nothing: every field is supplied there, so
   * every field is bounded.
   */
  unchanged?: { start?: boolean; end?: boolean };
};

/**
 * Milliseconds for a `Date`, an ISO string, or nothing. `null` when absent or
 * unparseable — never NaN, so callers can compare the results directly.
 *
 * `Event.start`/`end` are TYPED `Date` but are ISO STRINGS at runtime until
 * the first edit, and the persisted baseline itself flips string -> Date after
 * a save. Comparing those two shapes with `===` (or `getTime()` against a
 * string) reports an unmoved date as changed, which put an untouched `start`
 * into the payload where the backend's past-date bound then rejected the whole
 * PUT. Every "did this date move?" question goes through here.
 */
export function toInstantMs(
  value: Date | string | null | undefined,
): number | null {
  if (value == null || value === "") return null;
  const ms = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/** Instant equality across the `Date | ISO string | undefined` type lie. Both
 *  absent counts as equal; an unparseable value equals only another absent or
 *  unparseable one. */
export function isSameInstant(
  a: Date | string | null | undefined,
  b: Date | string | null | undefined,
): boolean {
  return toInstantMs(a) === toInstantMs(b);
}

/**
 * Returns the offending `errors`-namespace key, or `null` when the range is
 * valid. Missing or unparseable start/end is treated as "not yet a violation"
 * (returns `null`) — callers validate presence/parseability separately, exactly
 * as the two form validators did before centralisation.
 */
export function validateEventDateBounds(
  start: Date | string | null | undefined,
  end: Date | string | null | undefined,
  opts: EventDateBoundsOptions = {},
): EventDateBoundsError | null {
  if (start == null || end == null || start === "" || end === "") return null;

  const startDate = new Date(start);
  const endDate = new Date(end);
  // Unparseable input is not a range violation — surfaced elsewhere.
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()))
    return null;

  const now = opts.now ?? new Date();
  const earliest = new Date(now.getTime() - EVENT_DATE_PAST_SLACK_MS);
  const latest = new Date(now.getTime() + EVENT_DATE_FUTURE_CAP_MS);
  const planCap =
    opts.planExpiresAt != null && opts.planExpiresAt !== ""
      ? new Date(opts.planExpiresAt)
      : null;

  // Suppression happens INSIDE the ladder, never on its result: only the FIRST
  // violation is returned, so filtering `startDateInPast` out of the return
  // value would hide a real `endDateInPast` sitting behind it.
  const skipStart = opts.unchanged?.start === true;
  const skipEnd = opts.unchanged?.end === true;

  // Ordering first, and never skipped — see `unchanged` above.
  if (startDate > endDate) return "startDateEndDate";
  if (!skipStart) {
    if (startDate < earliest) return "startDateInPast";
    if (startDate > latest) return "startDateTooFar";
  }
  if (!skipEnd) {
    if (endDate < earliest) return "endDateInPast";
    if (endDate > latest) return "endDateTooFar";
  }
  if (
    planCap &&
    !Number.isNaN(planCap.getTime()) &&
    ((!skipStart && startDate > planCap) || (!skipEnd && endDate > planCap))
  )
    return "eventDateAfterPlanExpiry";
  return null;
}

/**
 * Mirror of backend EventService.DATE_JUMP_THRESHOLD (14 days).
 *
 * A "big forward jump" is the re-run-a-finished-event signal that the server
 * allows once and then answers with 409 dateJumpLimitReached, steering the user
 * to Duplicate (fresh guest list + statistics) instead of re-dating the
 * original. Two shapes, both measured against the event's PREVIOUS end:
 *   (a) the new start lands more than the threshold past it;
 *   (b) the event had ALREADY ENDED and its end is pushed that far beyond it —
 *       judged however the payload is shaped.
 * The server is the source of truth; this copy exists so the cockpit can warn
 * BEFORE spending the one allowed jump instead of surfacing a bare 409.
 */
export const DATE_JUMP_THRESHOLD_MS = 14 * 24 * 60 * 60 * 1000;

/**
 * Mirror of backend EventService.DATE_JUMP_LIMIT — how many big forward jumps
 * an event gets before the server refuses with 409 and the cockpit steers to
 * Duplicate. Lives here with the threshold because a bare `1` had already been
 * hand-copied to three call sites.
 */
export const DATE_JUMP_LIMIT = 1;

/**
 * Would this PUT payload count as a big forward jump against the stored event?
 *
 * Keyed off the PAYLOAD, not off the draft: a date the user did not move is not
 * sent, and the server never judges what it does not receive. Lives here rather
 * than in one screen because the dashboard's inline event editor sends exactly
 * the same partial and must warn about exactly the same thing.
 */
export function isBigForwardJump(
  changes: { start?: Date | string; end?: Date | string },
  storedEnd: Date | string | null | undefined,
  now: Date = new Date(),
): boolean {
  const oldEnd = toInstantMs(storedEnd);
  if (oldEnd === null) return false;
  const threshold = oldEnd + DATE_JUMP_THRESHOLD_MS;

  const newStart = toInstantMs(changes.start);
  if (newStart !== null && newStart > threshold) return true;

  const newEnd = toInstantMs(changes.end);
  return (
    newEnd !== null && oldEnd < now.getTime() && newEnd > threshold
  );
}
